import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { ServiceOptionPage, ServiceOptionStatus } from '../serviceOrders/serviceOptions/serviceOption.types';
import {
    CompletionInfoForExecutionCreatedFromServiceOption,
    StartInfoForExecutionCreatedFromServiceOption,
} from '../serviceOrders/serviceOptions/serviceOrderCreationDetails.types';
import { ServiceOptionsTableColumns } from '../serviceOrders/serviceOptions/table/ServiceOptionsTableColumns';
import { ServiceType } from '../serviceOrders/shared/commonServiceOrder.types';
import { TableSort } from '../sharedComponents/table/SortedTableHeader';
import {
    mapToApiCompletionInfoForExecutionCreatedFromServiceOption,
    mapToApiStartInfoForExecutionCreatedFromServiceOption,
} from './mappers/serviceOrder/serviceOrderCreationMapper';
import { mapToServiceOptionQueryParameters } from './mappers/serviceOrder/serviceOptionQueryParamsMapper';
import { mapToServiceOptions } from './mappers/serviceOrder/serviceOptionsMapper';
import { decodeResponse, validateResponse } from './responseUtil';
import { serviceOrderApi, serviceOrderTag } from './serviceOrdersApi';
import { ServiceOptionPageCodec } from './types/serviceOrder/serviceOptionsApi.types';
import { prepareHeaders } from './utils';

export const SERVICE_OPTIONS_PATH = 'service-options';
const STARTED_SERVICE_ORDERS_SUBRESOURCE_PATH = 'started-service-orders';
const COMPLETED_SERVICE_ORDERS_SUBRESOURCE_PATH = 'completed-service-orders';

export type GetServiceOptionsQueryParams = {
    location?: string;
    statuses?: ServiceOptionStatus[];
    serviceTypes?: ServiceType[];
    orderIssuedAtAfter?: Date;
    orderIssuedAtBefore?: Date;
    searchTerm?: string;
    sortBy?: TableSort<ServiceOptionsTableColumns>;
    limit: number;
};

export type PostCreateStartedServiceOrderProps = {
    serviceOptionId: string;
    startInfo: StartInfoForExecutionCreatedFromServiceOption;
};

export type PostCreateCompletedServiceOrderProps = {
    serviceOptionId: string;
    completion: CompletionInfoForExecutionCreatedFromServiceOption;
};

const serviceOptionTag = 'ServiceOption';

export const serviceOptionApi = createApi({
    reducerPath: 'serviceOptionApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.FINISHED_VEHICLES_SERVICE,
        prepareHeaders,
    }),
    tagTypes: [serviceOptionTag],
    endpoints: (builder) => ({
        getServiceOptions: builder.query<ServiceOptionPage, GetServiceOptionsQueryParams>({
            query: (queryParameters) => ({
                url: SERVICE_OPTIONS_PATH,
                params: mapToServiceOptionQueryParameters(queryParameters),
                validateStatus: validateResponse(ServiceOptionPageCodec),
            }),
            transformResponse: decodeResponse(ServiceOptionPageCodec, mapToServiceOptions),
            providesTags: [serviceOptionTag],
        }),
        postCreateStartedServiceOrder: builder.mutation<void, PostCreateStartedServiceOrderProps>({
            query: ({ serviceOptionId, startInfo }) => ({
                url: `${SERVICE_OPTIONS_PATH}/${serviceOptionId}/${STARTED_SERVICE_ORDERS_SUBRESOURCE_PATH}`,
                method: 'POST',
                body: mapToApiStartInfoForExecutionCreatedFromServiceOption(startInfo),
            }),
            onQueryStarted: async (arg, api) => {
                // The service orders are their own API slice. Therefore, we can not use the declarative
                // invalidatesTags list approach but must manually dispatch the invalidation instead. We
                // ignore errors, because the code is simpler this way.
                // In case we have more places where this becomes necessary, we can also use Code Splitting:
                // https://redux-toolkit.js.org/rtk-query/usage/code-splitting
                await api.queryFulfilled.catch(() => {});
                api.dispatch(serviceOrderApi.util.invalidateTags([serviceOrderTag]));
            },
        }),
        postCreateCompletedServiceOrder: builder.mutation<void, PostCreateCompletedServiceOrderProps>({
            query: ({ serviceOptionId, completion }) => ({
                url: `${SERVICE_OPTIONS_PATH}/${serviceOptionId}/${COMPLETED_SERVICE_ORDERS_SUBRESOURCE_PATH}`,
                method: 'POST',
                body: mapToApiCompletionInfoForExecutionCreatedFromServiceOption(completion),
            }),
            onQueryStarted: async (arg, api) => {
                // The service orders are their own API slice. Therefore, we can not use the declarative
                // invalidatesTags list approach but must manually dispatch the invalidation instead. We
                // ignore errors, because the code is simpler this way.
                // In case we have more places where this becomes necessary, we can also use Code Splitting:
                // https://redux-toolkit.js.org/rtk-query/usage/code-splitting
                await api.queryFulfilled.catch(() => {});
                api.dispatch(serviceOrderApi.util.invalidateTags([serviceOrderTag]));
            },
        }),
    }),
});

export const {
    useGetServiceOptionsQuery,
    usePostCreateStartedServiceOrderMutation,
    usePostCreateCompletedServiceOrderMutation,
} = serviceOptionApi;

export const matchServiceOptionReceived = serviceOptionApi.endpoints.getServiceOptions.matchFulfilled;
